import React, { memo, useMemo, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { Chart as ChartJS, ChartOptions, Plugin } from 'chart.js';

import * as C from './Charts.styled';
import {
  CHART_COMPARE_DASH,
  CHART_GRID,
  CHART_INK,
  CHART_NO_ANIMATION,
  CHART_TICK_FONT,
  CHART_TONES,
  chartBaseTooltip,
  chartFaded,
} from './chartTokens';
import { registerCharts } from './registerCharts';

registerCharts();

export interface IBubblePoint {
  /** Ключ точки — уходит в `onSelect`. */
  id: string;
  label: string;
  /** `null` — точку не нарисовать (расход неизвестен). */
  x: number | null;
  y: number | null;
  /** Величина радиуса в единицах данных (продажи); 0 — минимальный кружок. */
  r: number;
  color: string;
}

export interface IBubbleDatum {
  x: number;
  y: number;
  /** Радиус в пикселях. */
  r: number;
  id: string;
  label: string;
}

/** Медиана; пусто — `null`. */
export const chartMedian = (values: ReadonlyArray<number>): number | null => {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/**
 * Точки → данные пузырьков. Радиус — по корню из величины (площадь кружка
 * пропорциональна числу), в пределах `[minRadius, maxRadius]`. Точки без
 * координат и, на логарифмической оси, с x ≤ 0 не рисуются — их считает
 * `skipped`, чтобы экран сказал «ещё N без расхода».
 */
export const bubbleData = (
  points: ReadonlyArray<IBubblePoint>,
  { logX = false, minRadius = 4, maxRadius = 22 } = {},
): {
  data: IBubbleDatum[];
  colors: string[];
  skipped: number;
} => {
  const drawable = points.filter(
    (point) =>
      point.x !== null && point.y !== null && (!logX || (point.x ?? 0) > 0),
  );
  const top = Math.max(0, ...drawable.map((point) => point.r));
  return {
    data: drawable.map((point) => ({
      x: point.x as number,
      y: point.y as number,
      r:
        top > 0
          ? minRadius + (maxRadius - minRadius) * Math.sqrt(point.r / top)
          : minRadius,
      id: point.id,
      label: point.label,
    })),
    colors: drawable.map((point) => point.color),
    skipped: points.length - drawable.length,
  };
};

export interface IBubbleGuide {
  /** Наклон линии y = slope · x (лиды = расход / медианный CPL). */
  slope: number;
  color?: string;
}

/**
 * Встроенная направляющая: прямая через ноль поверх точек. Плагины
 * react-chartjs-2 берёт только при создании графика — поэтому значение
 * читается при каждой отрисовке (`read`), а не замыкается: иначе после смены
 * фильтра линия рисовала бы старую медиану.
 */
const guidePlugin = (read: () => IBubbleGuide | null | undefined): Plugin => ({
  id: 'bubbleGuide',
  afterDatasetsDraw: (chart: ChartJS) => {
    const guide = read();
    if (!guide || !Number.isFinite(guide.slope) || guide.slope <= 0) return;
    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    const yScale = scales.y;
    if (!xScale || !yScale) return;
    const x0 = Math.max(xScale.min, 1e-9);
    const x1 = xScale.max;
    ctx.save();
    ctx.beginPath();
    ctx.rect(
      chartArea.left,
      chartArea.top,
      chartArea.right - chartArea.left,
      chartArea.bottom - chartArea.top,
    );
    ctx.clip();
    ctx.strokeStyle = guide.color ?? CHART_INK;
    ctx.lineWidth = 1;
    ctx.setLineDash(CHART_COMPARE_DASH);
    ctx.beginPath();
    // Логарифмическая ось — ломаная из шагов, чтобы прямая выглядела прямой.
    const steps = 24;
    for (let i = 0; i <= steps; i += 1) {
      const x = x0 + ((x1 - x0) * i) / steps;
      const px = xScale.getPixelForValue(x);
      const py = yScale.getPixelForValue(x * guide.slope);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
    ctx.restore();
  },
});

/**
 * Выделенная область графика (квадрант «много трафика, слабая конверсия»):
 * границы в единицах данных, `null` — до края области графика.
 */
export interface IBubbleRegion {
  xFrom?: number | null;
  xTo?: number | null;
  yFrom?: number | null;
  yTo?: number | null;
  color: string;
  /** Подпись в углу области (рисуется поверх заливки). */
  label?: string;
}

/** Самая узкая полоса области, px: квадрант «конверсия ≤ 0» не исчезает. */
export const BUBBLE_REGION_MIN_PX = 14;

/**
 * Прямоугольник области в пикселях внутри области графика; область вне
 * видимых осей — `null`. Область, прижатая к низу (или левому краю) и
 * схлопнувшаяся в линию (медиана конверсии — 0), — полоса не уже
 * `BUBBLE_REGION_MIN_PX`: страницы на нуле в ней видны.
 */
export const bubbleRegionRect = (
  region: IBubbleRegion,
  area: { left: number; right: number; top: number; bottom: number },
  toX: (value: number) => number,
  toY: (value: number) => number,
): { x: number; y: number; width: number; height: number } | null => {
  const clampX = (px: number) => Math.min(Math.max(px, area.left), area.right);
  const clampY = (px: number) => Math.min(Math.max(px, area.top), area.bottom);
  const left =
    region.xFrom === null || region.xFrom === undefined
      ? area.left
      : clampX(toX(region.xFrom));
  const right =
    region.xTo === null || region.xTo === undefined
      ? area.right
      : clampX(toX(region.xTo));
  // Ось y растёт вверх: «от» — нижняя граница, «до» — верхняя.
  const bottom =
    region.yFrom === null || region.yFrom === undefined
      ? area.bottom
      : clampY(toY(region.yFrom));
  const top =
    region.yTo === null || region.yTo === undefined
      ? area.top
      : clampY(toY(region.yTo));
  const anchoredBottom = region.yFrom === null || region.yFrom === undefined;
  const anchoredLeft = region.xFrom === null || region.xFrom === undefined;
  const width =
    anchoredLeft && right - left >= 0
      ? Math.max(right - left, BUBBLE_REGION_MIN_PX)
      : right - left;
  let y = top;
  let height = bottom - top;
  if (anchoredBottom && height >= 0 && height < BUBBLE_REGION_MIN_PX) {
    height = Math.min(BUBBLE_REGION_MIN_PX, bottom - area.top);
    y = bottom - height;
  }
  if (!(width > 0) || !(height > 0)) return null;
  return { x: left, y, width, height };
};

/** Встроенная заливка выделенной области под точками (значение — `read`). */
const regionPlugin = (
  read: () => IBubbleRegion | null | undefined,
): Plugin => ({
  id: 'bubbleRegion',
  beforeDatasetsDraw: (chart: ChartJS) => {
    const region = read();
    if (!region) return;
    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    const yScale = scales.y;
    if (!xScale || !yScale) return;
    const rect = bubbleRegionRect(
      region,
      chartArea,
      (value) => xScale.getPixelForValue(value),
      (value) => yScale.getPixelForValue(value),
    );
    if (!rect) return;
    ctx.save();
    ctx.fillStyle = chartFaded(region.color, 0.12);
    ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
    ctx.strokeStyle = chartFaded(region.color, 0.5);
    ctx.setLineDash(CHART_COMPARE_DASH);
    ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
    if (region.label) {
      ctx.setLineDash([]);
      ctx.fillStyle = region.color;
      ctx.font = '600 11px sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText(
        region.label,
        rect.x + rect.width - 6,
        rect.y + rect.height - 4,
      );
    }
    ctx.restore();
  },
});

/** Длиннее — подпись пузыря обрезается многоточием (полное — в подсказке). */
export const BUBBLE_LABEL_MAX = 28;

/** Подпись пузыря: имя, обрезанное до `BUBBLE_LABEL_MAX` символов. */
export const bubbleLabelText = (label: string): string =>
  label.length > BUBBLE_LABEL_MAX
    ? `${label.slice(0, BUBBLE_LABEL_MAX - 1).trimEnd()}…`
    : label;

/**
 * ТЗ 0103, пакет 2 — подписи у выбранных пузырей (крупнейших): имя справа
 * от кружка, у правого края — слева, с белой обводкой поверх сетки. Порядок
 * `ids` — приоритет: подпись, которая наехала бы на уже нарисованную,
 * пропускается (её имя — в подсказке точки).
 */
const labelsPlugin = (
  read: () => {
    ids: ReadonlyArray<string> | null | undefined;
    data: ReadonlyArray<IBubbleDatum>;
  },
): Plugin => ({
  id: 'bubbleLabels',
  afterDatasetsDraw: (chart: ChartJS) => {
    const { ids, data } = read();
    if (!ids || !ids.length) return;
    const meta = chart.getDatasetMeta(0);
    const { ctx, chartArea } = chart;
    const indexOf = new Map(data.map((datum, index) => [datum.id, index]));
    type Box = { x0: number; x1: number; y0: number; y1: number };
    const hits = (a: Box, b: Box) =>
      a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;
    // Кружки — тоже препятствия: подпись не должна лечь на соседний пузырь.
    const bubbles: Array<Box & { index: number }> = data.flatMap(
      (datum, index) => {
        const element = meta.data[index] as
          | { x: number; y: number }
          | undefined;
        if (!element || !Number.isFinite(element.x)) return [];
        return [
          {
            index,
            x0: element.x - datum.r,
            x1: element.x + datum.r,
            y0: element.y - datum.r,
            y1: element.y + datum.r,
          },
        ];
      },
    );
    const placed: Box[] = [];
    ctx.save();
    ctx.font = '600 11px sans-serif';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#fff';
    ctx.fillStyle = CHART_INK;
    ids.forEach((id) => {
      const index = indexOf.get(id);
      if (index === undefined) return;
      const datum = data[index];
      const element = meta.data[index] as { x: number; y: number } | undefined;
      if (!element || !Number.isFinite(element.x)) return;
      const text = bubbleLabelText(datum.label);
      const { width } = ctx.measureText(text);
      const boxAt = (left: number, y: number): Box => ({
        x0: left,
        x1: left + width,
        y0: y - 7,
        y1: y + 7,
      });
      // Справа от кружка, слева, над ним, под ним — первое место без
      // соседнего пузыря и без другой подписи; нет такого — без пузырей не
      // вышло, тогда хотя бы без наложения подписей.
      const centered = element.x - width / 2;
      // Сбоку — на высоте центра (у верхней кромки тоже); над и под — только
      // внутри области графика.
      const inside = (box: Box) =>
        box.y0 >= chartArea.top && box.y1 <= chartArea.bottom;
      const candidates = [
        boxAt(element.x + datum.r + 4, element.y),
        boxAt(element.x - datum.r - 4 - width, element.y),
        ...[
          boxAt(centered, element.y - datum.r - 9),
          boxAt(centered, element.y + datum.r + 9),
        ].filter(inside),
      ].filter(
        (box) =>
          box.x0 >= chartArea.left &&
          box.x1 <= chartArea.right &&
          !placed.some((other) => hits(box, other)),
      );
      const free = (box: Box) =>
        !bubbles.some((other) => other.index !== index && hits(box, other));
      const box = candidates.find(free) ?? candidates[0];
      if (!box) return;
      placed.push(box);
      const y = (box.y0 + box.y1) / 2;
      ctx.textAlign = 'left';
      ctx.strokeText(text, box.x0, y);
      ctx.fillText(text, box.x0, y);
    });
    ctx.restore();
  },
});

export interface IBubbleChartProps {
  points: ReadonlyArray<IBubblePoint>;
  xLabel: string;
  yLabel: string;
  formatX: (value: number) => string;
  formatY: (value: number) => string;
  /** Строка подсказки точки (числа строкой). */
  tooltipOf?: (id: string) => string[];
  logX?: boolean;
  guide?: IBubbleGuide | null;
  /** Выделенная область (квадрант) под точками. */
  region?: IBubbleRegion | null;
  /**
   * ТЗ 0103, пакет 2: подписать эти точки именем (крупнейшие пузыри);
   * порядок — приоритет при наложении подписей.
   */
  labelIds?: ReadonlyArray<string> | null;
  onSelect?: (id: string) => void;
  ariaLabel?: string;
}

/**
 * ТЗ 0103 — пузырьки (`<Chart type="bubble">`, моки jest знают `Chart`):
 * x — расход, y — лиды, радиус — продажи, цвет — исход. Направляющая —
 * медианный CPL: ниже линии лид дороже медианы.
 */
export const BubbleChart = memo(
  ({
    points,
    xLabel,
    yLabel,
    formatX,
    formatY,
    tooltipOf,
    logX = false,
    guide,
    region,
    labelIds,
    onSelect,
    ariaLabel,
  }: IBubbleChartProps) => {
    const built = useMemo(() => bubbleData(points, { logX }), [points, logX]);
    const data = useMemo(
      () => ({
        datasets: [
          {
            label: yLabel,
            data: built.data,
            backgroundColor: built.colors.map((color) =>
              chartFaded(color, 0.6),
            ),
            borderColor: built.colors,
            borderWidth: 1,
            hoverBorderWidth: 2,
          },
        ],
      }),
      [built, yLabel],
    );
    // Плагины создаются один раз (react-chartjs-2 их не обновляет) и читают
    // текущие направляющую, область и подписи отсюда.
    const live = useRef({ guide, region, labelIds, data: built.data });
    live.current = { guide, region, labelIds, data: built.data };
    const plugins = useMemo(
      () => [
        regionPlugin(() => live.current.region),
        guidePlugin(() => live.current.guide),
        labelsPlugin(() => ({
          ids: live.current.labelIds,
          data: live.current.data,
        })),
      ],
      [],
    );
    const options: ChartOptions<'bubble'> = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: CHART_NO_ANIMATION,
        onClick: (_event, elements) => {
          const [first] = elements;
          const datum = first ? built.data[first.index] : null;
          if (datum && onSelect) onSelect(datum.id);
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...chartBaseTooltip,
            callbacks: {
              title: (items) => {
                const [first] = items;
                return first ? built.data[first.dataIndex]?.label ?? '' : '';
              },
              label: (item) => {
                const datum = built.data[item.dataIndex];
                if (!datum) return '';
                if (tooltipOf) return tooltipOf(datum.id);
                return `${xLabel}: ${formatX(datum.x)} · ${yLabel}: ${formatY(
                  datum.y,
                )}`;
              },
            },
          },
        },
        scales: {
          x: {
            type: logX ? 'logarithmic' : 'linear',
            title: {
              display: true,
              text: xLabel,
              color: CHART_INK,
              font: CHART_TICK_FONT,
            },
            grid: { color: CHART_GRID },
            ticks: {
              font: CHART_TICK_FONT,
              color: CHART_INK,
              maxTicksLimit: 8,
              callback: (value) => formatX(Number(value)),
            },
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: yLabel,
              color: CHART_INK,
              font: CHART_TICK_FONT,
            },
            grid: { color: CHART_GRID },
            ticks: {
              font: CHART_TICK_FONT,
              color: CHART_INK,
              callback: (value) => formatY(Number(value)),
            },
          },
        },
      }),
      [built, xLabel, yLabel, formatX, formatY, tooltipOf, logX, onSelect],
    );
    return (
      <C.ChartBox role="img" aria-label={ariaLabel}>
        <Chart
          type="bubble"
          data={data as never}
          options={options as never}
          plugins={plugins as never}
        />
      </C.ChartBox>
    );
  },
);

BubbleChart.displayName = 'BubbleChart';

/** Цвет исхода точки: есть продажи / лиды без продаж / без лидов / без расхода. */
export const BUBBLE_OUTCOME_COLORS = {
  sales: CHART_TONES.good,
  leadsNoSales: CHART_TONES.warn,
  noLeads: CHART_TONES.bad,
  noSpend: CHART_TONES.neutral,
} as const;
